// Console owner setup: pricing, settings, fleet, team, catalog.
// Keys are the Arabic source strings (what the Arabic UI shows); values are English.
// Strings that already exist in the prototype catalog (en.ts) need no entry here.
export const consoleSetupEn: Readonly<Record<string, string>> = {
  // Shared
  تعديل: 'Edit',
  إلغاء: 'Cancel',
  تم: 'Done',
  مغلق: 'Closed',
  مفتوح: 'Open',
  الوصف: 'Description',
  اختياري: 'Optional',
  'حُفظت التغييرات': 'Changes saved',
  تجاهل: 'Discard',
  'نعم، احذف': 'Yes, delete',
  'الشهر السابق': 'Previous month',
  'الشهر التالي': 'Next month',
  موقوفة: 'Off',
  نسخ: 'Copy',
  'تعذّر النسخ': "Couldn't copy",
  مثال: 'Example',
  دقيقة: 'min',
  شهر: 'months',
  'الاسم بالإنجليزية': 'Name in English',
  'الوصف بالإنجليزية': 'Description in English',
  الترتيب: 'Order',
  'الأصغر يظهر أولاً': 'Lower numbers show first',
  'تغييرات غير محفوظة': 'Unsaved changes',
  زيادة: 'up',
  نقص: 'down',

  // Pricing page
  'كل تغيير هنا يظهر للزبائن في موقع الحجز': 'Every change here shows on the booking site',
  'أسعار السيارات': 'Car prices',
  المواسم: 'Seasons',
  'أيام الإغلاق': 'Closed days',
  'قواعد الحجز': 'Booking rules',
  الموديلات: 'Models',
  'البقاء في الصفحة': 'Stay on this page',
  'المغادرة دون حفظ': 'Leave without saving',
  'لديك تغييرات على الأسعار أو القواعد لم تُحفظ بعد. إذا غادرت ستضيع.':
    "You have price or rule changes that aren't saved yet. If you leave, they will be lost.",

  // Car prices
  'أيام نهاية الأسبوع': 'Weekend days',
  'نُشرت الأسعار الجديدة على موقع الحجز': 'The new prices are live on the booking site',
  'الأسعار تشمل الشحن والتأمين الأساسي. تسعير الموسم يُضاف تلقائياً فوقها.':
    'Prices include charging and basic insurance. Seasonal pricing is added on top automatically.',
  'حُدّثت الأسعار من جهاز آخر. راجع الأسعار المعروضة ثم احفظ مجدداً.':
    'Prices were updated from another device. Check the prices shown, then save again.',
  'أدخل سعراً صحيحاً أكبر من صفر في كل الخانات المعلّمة.':
    'Enter a valid price above zero in every marked field.',
  '{n} تغييرات على الأسعار لم تُحفظ بعد': '{n} price changes not saved yet',
  'حفظ ونشر الأسعار': 'Save and publish prices',
  'سعر اليوم الكامل أقل من النهاري': 'The full-day price is lower than the day price',
  'غير محفوظ': 'Unsaved',
  'الزبون يدفع {p} عند الحجز والباقي عند الاستلام':
    'The customer pays {p} when booking and the rest at pickup',
  'أدخل سعراً صحيحاً': 'Enter a valid price',

  // Seasons
  'لا توجد مواسم بعد. أضف موسماً لرفع الأسعار في فترات الذروة.':
    'No seasons yet. Add one to raise prices at peak times.',
  'متى يُطبّق الموسم': 'When the season applies',
  'حسب التاريخ': 'By dates',
  'مفعّل دائماً': 'Always on',
  '{n} تغييرات على المواسم لم تُحفظ بعد': '{n} season changes not saved yet',
  'نُشرت المواسم على موقع الحجز': 'The seasons are live on the booking site',
  'حفظ ونشر': 'Save and publish',
  'مفعّل اليوم': 'Active today',
  'غير مفعّل اليوم': 'Not active today',
  'نسبة الزيادة': 'Uplift',
  'مثال: عيد الفطر': 'e.g. Eid al-Fitr',
  'أُضيف الموسم': 'Season added',
  'موسم جديد': 'New season',
  'إضافة الموسم': 'Add season',
  'تعديل الموسم': 'Edit season',
  'حذف الموسم': 'Delete season',
  'حذف الموسم؟': 'Delete this season?',
  'حُذف الموسم': 'Season deleted',
  'الحجوزات القائمة تبقى بأسعارها. الحجوزات الجديدة لن تُرفع أسعارها في هذه الفترة.':
    "Existing bookings keep their prices. New bookings won't be raised in this period.",

  // Add-ons and packs
  'لا توجد إضافات بعد': 'No add-ons yet',
  الباقات: 'Packs',
  'باقة جديدة': 'New pack',
  'مجموعة إضافات تُضاف للحجز بضغطة واحدة، مع خصم اختياري.':
    'A set of add-ons added to a booking in one tap, with an optional discount.',
  'لا توجد باقات بعد': 'No packs yet',
  '{n} تغييرات على أسعار الإضافات لم تُحفظ بعد': '{n} add-on price changes not saved yet',
  'نُشرت أسعار الإضافات على موقع الحجز': 'The add-on prices are live on the booking site',
  'قيمة الشراء': 'Retail value',
  المتوفر: 'In stock',
  'إظهار في موقع الحجز': 'Show on the booking site',
  'أُضيفت الخدمة': 'Add-on added',
  'إضافة خدمة جديدة': 'Add a new add-on',
  'ماذا تتضمن': "What's included",
  'الكمية المتوفرة': 'Quantity available',
  'إضافة الخدمة': 'Add add-on',
  شواية: 'Grill',
  قدر: 'Pot',
  كرسي: 'Chair',
  ثلاجة: 'Cooler',
  حامل: 'Mount',
  شحن: 'Charging',
  مقعد: 'Seat',
  الأيقونة: 'Icon',
  'تعديل الإضافة': 'Edit add-on',
  'حذف الإضافة': 'Delete add-on',
  'تظهر للزبون كمقارنة. اتركها فارغة لإخفائها.': 'Shown to customers for comparison. Leave empty to hide it.',
  'كم حجزاً في نفس الوقت يمكنه أخذها': 'How many bookings can take it at the same time',
  'حذف الإضافة؟': 'Delete this add-on?',
  'حُذفت الإضافة': 'Add-on deleted',
  'إن كانت مستخدمة في حجوزات أو باقات فأوقفها بدلاً من حذفها.':
    'If bookings or packs use it, switch it off instead of deleting it.',
  'حُفظت الباقة': 'Pack saved',
  'تعديل الباقة': 'Edit pack',
  'حفظ الباقة': 'Save pack',
  'حذف الباقة': 'Delete pack',
  'مثال: باقة الكشتة': 'e.g. Outing package',
  'خصم الباقة (%)': 'Pack discount (%)',
  '0 = بدون خصم': '0 = no discount',
  'إضافات الباقة': 'Add-ons in the pack',
  'اختر إضافة واحدة على الأقل': 'Choose at least one add-on',
  'حذف الباقة؟': 'Delete this pack?',
  'حُذفت الباقة': 'Pack deleted',

  // Closed days
  'أُغلق {d} للحجوزات الجديدة. {n} حجوزات قائمة تبقى كما هي.':
    '{d} is closed to new bookings. {n} existing bookings stay as they are.',
  'أُغلق {d} للحجوزات الجديدة': '{d} is closed to new bookings',
  'فُتح {d} للحجز': '{d} is open for booking',
  'أيام مغلقة': 'closed days',
  'كل الأيام مفتوحة للحجز': 'Every day is open for booking',
  'إغلاق يوم يمنع الحجوزات الجديدة فقط. الحجوزات القائمة تبقى كما هي.':
    'Closing a day only stops new bookings. Existing bookings stay as they are.',

  // Promo codes
  منتهي: 'Expired',
  'لم يبدأ بعد': 'Not started',
  'لا توجد أكواد خصم بعد': 'No promo codes yet',
  'نُسخ الكود': 'Code copied',
  'تفعيل الكود': 'Turn on code',
  'مضى من مدة الكود': 'Time elapsed of the code',
  مضى: 'Elapsed',
  استخدام: 'uses',
  'نسبة الخصم': 'Discount',
  'أُنشئ الكود {code}': 'Code {code} created',
  'كود خصم جديد': 'New promo code',
  توليد: 'Generate',
  'حد الاستخدامات': 'Use limit',
  'لكل زبون': 'Per customer',
  'تعديل الكود': 'Edit code',
  'حذف الكود': 'Delete code',
  'نسبة الخصم (%)': 'Discount (%)',
  'فارغ = بلا حد': 'Empty = no limit',
  استُخدم: 'Used',
  مرة: 'times',
  'حذف الكود؟': 'Delete this code?',
  'حُذف الكود': 'Code deleted',
  'أوقف الكود بدلاً من حذفه.': 'Switch the code off instead of deleting it.',
  'لن يعود الكود صالحاً في موقع الحجز.': 'The code will no longer work on the booking site.',

  // Booking rules
  'حُفظت الإعدادات': 'Settings saved',
  'ما يدفعه الزبون أونلاين لتأكيد الحجز. الباقي عند الاستلام.':
    'What the customer pays online to confirm. The rest at pickup.',
  'حجز بقيمة': 'a booking of',
  'الدفع الإلكتروني': 'Online payment',
  'ما يختاره الزبون في موقع الحجز، ومدة حجز السيارة له أثناء الدفع.':
    'What customers can choose on the booking site, and how long the car is held while they pay.',
  'قبول الدفع أونلاين': 'Accept online payment',
  'خطط الدفع': 'Payment plans',
  'مهلة الدفع': 'Payment hold',
  'لكل ساعة تأخير بعد نهاية الفترة النهارية.': 'Per hour late after the end of the day rental.',
  'الرسوم لكل ساعة': 'Fee per hour',
  'مهلة السماح': 'Grace period',
  'التأخير في إيجار اليوم الكامل': 'Late returns on full-day rentals',
  'ماذا يحدث إذا أعاد الزبون السيارة بعد الموعد.': 'What happens when a customer brings the car back late.',
  'سياسة التأخير': 'Late policy',
  'بدون رسوم': 'No fee',
  بالساعة: 'Hourly',
  'بحد أقصى سعر يوم واحد': 'Capped at one day',
  'لا تتجاوز رسوم الساعات سعر يوم إضافي': "Hourly fees never exceed one extra day's price",
  'سياسة الإلغاء': 'Cancellation policy',
  'لا يُلغى الحجز. يمكن تحويله إلى رصيد بقيمة المدفوع عند الطلب قبل المهلة.':
    "Bookings aren't cancelled. They can be converted to credit for the amount paid, on request before the deadline.",
  'مهلة التحويل': 'Conversion deadline',
  'عند تغيير الموعد إلى سعر أقل': 'When a new time costs less',
  'يُضاف الفرق رصيداً': 'The difference becomes credit',
  'لا يُرد الفرق': 'No refund of the difference',
  'نسخة الشروط': 'Terms version',
  'غيّرها عند تعديل الشروط ليوافق الزبائن على النسخة الجديدة':
    'Change it when you edit the terms so customers accept the new version',
  'ساعات الإيجار النهاري': 'Day rental hours',
  'أوقات الاستلام والإرجاع للفترة النهارية (بتوقيت مسقط).':
    'Pickup and return times for day rentals (Muscat time).',
  'أول استلام': 'First pickup',
  'آخر استلام': 'Last pickup',
  'نهاية الفترة': 'Day ends',
  'آخر إرجاع': 'Latest return',
  'إيجار اليوم الكامل': 'Full-day rental',
  'أوقات الاستلام وعدد الأيام المسموح.': 'Pickup times and allowed number of days.',
  'أقل عدد أيام': 'Minimum days',
  'أكثر عدد أيام': 'Maximum days',
  'الشحن والتسليم': 'Charging and handover',
  'تُسلَّم السيارة بشحن كافٍ، وتُعاد بأي نسبة دون رسوم.':
    'Cars go out with enough charge and come back at any level with no fee.',
  'أقل شحن عند التسليم': 'Minimum charge at handover',
  'تنبيه الشحن تحت': 'Charging alert below',
  'وقت التجهيز بين حجزين': 'Turnaround between bookings',
  'عدم الحضور بعد': 'No-show after',
  'تظهر في الفواتير وملخص الحجز.': 'Shown on invoices and the booking summary.',
  'تطبيق الضريبة': 'Charge VAT',
  'الأسعار شاملة الضريبة': 'Prices include VAT',
  'السعر المعروض للزبون يشمل الضريبة': 'The price customers see includes VAT',
  'منها ضريبة': 'of which VAT',
  'تُضاف ضريبة': 'plus VAT',
  الرصيد: 'Credit',
  'رصيد الزبائن من تحويل الحجوزات والتعويضات.': 'Customer credit from converted bookings and compensation.',
  'صلاحية الرصيد': 'Credit expires after',
  '0 = لا ينتهي': '0 = never',
  'يُجمع مع كود الخصم': 'Can be combined with a promo code',
  'رسوم الاستلام': 'Return fees',
  'تُضاف عند الاستلام حسب حالة السيارة.': 'Added at the return depending on the car.',
  'تنظيف خفيف': 'Light cleaning',
  'تنظيف كامل': 'Full cleaning',
  'تنبيهات الصيانة': 'Maintenance alerts',
  'ينبّه النظام قبل موعد الخدمة بهذه المسافة.': 'The system alerts this many km before a service is due.',
  الإطارات: 'Tyres',
  'الأهداف والتقديرات': 'Goals and estimates',
  'هدف الإيراد في لوحة اليوم، وتقدير الرحلة في موقع الحجز.':
    'The revenue goal on the dashboard and the trip estimate on the booking site.',
  'هدف الإيراد الشهري': 'Monthly revenue goal',
  'طول الطريق إلى الجبل': 'Route length to the mountain',
  'المدى المستخدم من البطارية': 'Usable share of the range',
  المستندات: 'Documents',
  'صور البطاقة والرخصة التي يرفعها الزبائن.': 'ID card and licence photos customers upload.',
  'أكبر حجم للملف': 'Largest file',
  'أكبر دقة للصورة': 'Largest image',
  ميغابكسل: 'megapixels',
  'حتى {n}': 'Up to {n}',
  'مدة الاحتفاظ': 'Keep for',
  'أنواع الملفات المقبولة': 'Accepted file types',
  'رسائل واتساب': 'WhatsApp messages',
  'أوقف أي رسالة لا تريد إرسالها للزبائن أو الفريق.':
    "Switch off any message you don't want sent to customers or the team.",
  'وقت التذكير قبل الاستلام': 'Reminder time before pickup',
  'رسالة واتساب للزبون بعد الدفع': 'WhatsApp to the customer after payment',
  'تذكير قبل الاستلام': 'Pickup reminder',
  'في اليوم السابق للاستلام': 'The day before pickup',
  'عند كل دفعة على الحجز': 'On every payment for the booking',
  'استلام طلب الرصيد': 'Credit request received',
  'تأكيد للزبون أن طلبه وصل': 'Tells the customer their request arrived',
  'عند تحويل الحجز إلى رصيد': 'When a booking is converted to credit',
  'مع سبب الرفض': 'With the reason',
  'المسافة والشحن والرسوم بعد العودة': 'Distance, charge and fees after the return',
  'دعوة عضو': 'Team invite',
  'رابط تعيين كلمة المرور للموظف الجديد': 'Password link for a new team member',
  'رمز الدخول': 'Sign-in code',
  'رمز التحقق لصفحة حجوزاتي (لا يُنصح بإيقافه)':
    'Verification code for My bookings (not recommended to switch off)',
  'بيانات المنشأة': 'Business details',
  'تظهر في الفواتير والعقود والموقع.': 'Shown on invoices, contracts and the site.',
  'الاسم التجاري': 'Trade name',
  'الاسم التجاري بالإنجليزية': 'Trade name in English',
  'السجل التجاري': 'CR number',
  'الرقم الضريبي': 'VAT number',
  'العنوان بالإنجليزية': 'Address in English',
  واتساب: 'WhatsApp',
  'البريد الإلكتروني': 'Email',
  'رابط الموقع على الخريطة': 'Map link',
  'غيّر شخص آخر الإعدادات قبل قليل.': 'Someone else changed the settings a moment ago.',
  'تحميل أحدث نسخة مع الإبقاء على تعديلاتك': 'Load the latest and keep your edits',
  'بعض القيم غير صحيحة. راجع الحقول المعلّمة.': 'Some values are not valid. Check the marked fields.',
  '{n} تغييرات لم تُحفظ بعد': '{n} changes not saved yet',
  'حفظ الإعدادات': 'Save settings',

  // Models
  'الموديلات التي تظهر في موقع الحجز. الأسعار في تبويب أسعار السيارات.':
    'The models shown on the booking site. Prices are in the Car prices tab.',
  'موديل جديد': 'New model',
  'لا توجد موديلات بعد': 'No models yet',
  المدى: 'Range',
  'تظهر في موقع الحجز': 'Shown on the booking site',
  'متاحة للحجز أونلاين وحضورياً': 'Bookable online and at the desk',
  'مخفية ولا تُحجز': 'Hidden and not bookable',
  'تعديل الموديل': 'Edit model',
  'إضافة الموديل': 'Add model',
  الرمز: 'Code',
  'أحرف لاتينية صغيرة، مثل my': 'Lower-case letters, like my',
  الفئة: 'Trim',
  الصورة: 'Photo',
  الشكل: 'Body',
  الشارة: 'Badge',
  'مناسبة لـ': 'Suits',
  ثانية: 's',
  المقاعد: 'Seats',
  'تغيير الإطارات كل': 'Tyres every',
  'الفحص الدوري كل': 'Inspection every',

  // Fleet
  'تخطيط صيانة': 'Plan maintenance',
  'إضافة سيارة': 'Add a car',
  'سجل الصيانة': 'Maintenance log',
  'لا توجد سيارات بعد. أضف أول سيارة للأسطول.': 'No cars yet. Add the first car to the fleet.',
  'بدأ الشحن': 'Charging started',
  'تغيرت حالة السيارة': 'Car status changed',
  'تفاصيل السيارة': 'Car details',
  'تحت الشحن منذ': 'Charging since',
  مع: 'With',
  'الحجز التالي': 'Next booking',
  'تتغير حالتها تلقائياً عند الاستلام.': 'Its status changes automatically at the return.',
  'سجل الشهر': 'Month history',
  شحنات: 'charges',
  'لا حجوزات في هذا الشهر': 'No bookings this month',
  'جلسات الشحن': 'Charging sessions',
  'لا شحن في هذا الشهر': 'No charging this month',
  جارٍ: 'ongoing',
  الصيانة: 'Maintenance',
  'لا صيانة في هذا الشهر': 'No maintenance this month',
  منجز: 'Done',
  مخطط: 'Planned',
  'في الورشة': 'In the workshop',
  مفتوحة: 'Open',
  منجزة: 'Done',
  ملغاة: 'Cancelled',
  'كل السيارات': 'All cars',
  'لا توجد أعمال صيانة هنا': 'No maintenance here',
  'إلغاء الصيانة؟': 'Cancel this maintenance?',
  'نعم، ألغِ الصيانة': 'Yes, cancel it',
  'تعود السيارة متاحة للحجز في هذه الفترة.': 'The car becomes bookable again for this period.',
  'إلى الورشة': 'To the workshop',
  إصلاح: 'Repair',
  'انتهى الشحن، السيارة متاحة': 'Charging finished, the car is available',
  'إيقاف الشحن': 'Stop charging',
  'البطارية الآن': 'Battery now',
  'تكلفة الشحن': 'Charging cost',
  'سُجّلت الصيانة كمنجزة': 'Maintenance recorded as done',
  'إنهاء الصيانة': 'Finish maintenance',
  'العداد الآن': 'Odometer now',
  التكلفة: 'Cost',
  'سُجّلت الصيانة وأُعيد العداد': 'Service recorded and the counter reset',
  'تسجيل التنفيذ': 'Record as done',
  'سجّل التنفيذ بعد عودة السيارة': 'Record it once the car is back',
  'سجّل العمل الذي تم على السيارة ليبدأ العداد من جديد.':
    'Record the work done on the car so its counter starts again.',
  'تسجيل عمل منجز': 'Record work done',
  'تاريخ التنفيذ': 'Date done',
  'العداد عند التنفيذ': 'Odometer when done',
  'خُطّطت الصيانة، والسيارة محجوبة عن الحجز في هذه الفترة':
    'Maintenance planned; the car is blocked for bookings in this period',
  'تُحجب السيارة عن الحجز في هذه الفترة.': 'The car is blocked for bookings in this period.',
  'حفظ الموعد': 'Save',
  'نوع العمل': 'Work',
  الساعة: 'Time',
  'التكلفة المتوقعة': 'Expected cost',
  'أُضيفت السيارة إلى الأسطول': 'Car added to the fleet',
  'تعديل السيارة': 'Edit car',
  'إضافة السيارة': 'Add car',
  'رمز السيارة': 'Car code',
  'مثل Y3': 'like Y3',
  الموديل: 'Model',
  'رقم اللوحة': 'Plate',
  'رقم الهيكل (VIN)': 'VIN',
  اللون: 'Colour',
  'اللون بالإنجليزية': 'Colour in English',
  'درجة اللون': 'Colour swatch',
  'عداد آخر تغيير إطارات': 'Odometer at last tyre change',
  'عداد آخر فحص': 'Odometer at last inspection',

  // Team
  'إضافة عضو': 'Add member',
  الأعضاء: 'Members',
  'كل الصفحات والإجراءات': 'All pages and actions',
  صفحات: 'pages',
  إجراءات: 'actions',
  عضو: 'members',
  'بانتظار التفعيل': 'awaiting activation',
  أدوار: 'roles',
  'ابحث بالاسم أو اسم المستخدم': 'Search by name or username',
  'لا نتائج': 'No results',
  'حساب المالك لا يمكن تعطيله': "The owner's account can't be disabled",
  'لا يمكنك إيقاف حسابك': "You can't disable your own account",
  'بلا دور': 'No role',
  'بانتظار تفعيل الدعوة': 'Invite not accepted yet',
  'لم يسجّل الدخول بعد': 'Never signed in',
  'رابط الدعوة': 'Invite link',
  'أُرسل للعضو على واتساب إن كان رقمه مسجّلاً. صالح حتى':
    'Sent to the member on WhatsApp if their number is set. Valid until',
  'نُسخ الرابط': 'Link copied',
  'نسخ الرابط': 'Copy link',
  'أُنشئ الحساب. شارك رابط الدعوة مع العضو.': 'Account created. Share the invite link with the member.',
  'تمت إضافة العضو': 'Member added',
  'تعديل العضو': 'Edit member',
  'إضافة عضو للفريق': 'Add a team member',
  'حذف العضو': 'Delete member',
  'الاسم الكامل': 'Full name',
  'لا يتغير بعد الإنشاء': "Can't change after creation",
  'أحرف لاتينية وأرقام و . _ -': 'Latin letters, digits and . _ -',
  'تصل عليه الدعوة على واتساب': 'The invite arrives on WhatsApp',
  'سيصل للعضو رابط لتعيين كلمة المرور على هاتفه.':
    'The member will receive a link on their phone to set a password.',
  'لم يفعّل العضو حسابه بعد.': "The member hasn't activated their account yet.",
  'إرسال رابط دعوة جديد': 'Send a new invite link',
  'إرسال رابط لإعادة تعيين كلمة المرور': 'Send a password reset link',
  'تتغير صلاحياته فور الحفظ، ويُطلب منه الدخول من جديد إن لزم.':
    'Their permissions change on save; they may be asked to sign in again.',
  'حذف العضو؟': 'Delete this member?',
  'حُذف العضو': 'Member deleted',
  'حذف نهائي؟': 'Delete permanently?',
  'يبقى اسمه في سجل الأنشطة. لإيقافه مؤقتاً استخدم مفتاح التفعيل.':
    'Their name stays in the activity log. To pause the account, use the switch instead.',
  'حُذف هذا العضو': 'This member was deleted',
  'كل شيء': 'Everything',
  'أعضاء بهذا الدور': 'members with this role',
  'بلا أعضاء': 'No members',
  'نسخ الدور': 'Duplicate role',
  'نُسخ الدور: {name}': 'Role duplicated: {name}',
  'ابدأ من قالب': 'Start from a template',
  'أدوار جاهزة يمكنك تعديلها': 'Ready-made roles you can adjust',
  'حُفظ الدور': 'Role saved',
  'لا يمكن حذف دور مسند لأعضاء': "A role that members hold can't be deleted",
  'مثال: مشرف مسائي': 'e.g. Evening supervisor',
  'اسم الدور بالإنجليزية': 'Role name in English',
  'صلاحية مختارة': 'permissions selected',
  'اختر صلاحية واحدة على الأقل': 'Choose at least one permission',
  'إلغاء الكل': 'Clear all',
  'تحديد الكل': 'Select all',
  'حذف الدور؟': 'Delete this role?',
  'حُذف الدور': 'Role deleted',
  'انقل أعضاءه إلى دور آخر أولاً.': 'Move its members to another role first.',
  'لا يمكن التراجع عن الحذف.': "This can't be undone.",

  // Activity log
  العضو: 'Member',
  'نوع النشاط': 'Activity',
  'كل الأنشطة': 'All activity',
  'أنشطة الفريق فقط': 'Team activity only',
  'إخفاء ما يفعله النظام والزبائن': 'Hide what the system and customers do',
  'مسح الفلاتر': 'Clear filters',
  الحقول: 'Fields',
  زبون: 'Customer',
  النظام: 'System',
  المدفوعات: 'Payments',
  'الأسعار والأكواد': 'Prices and codes',
  'الإضافات والسيارات': 'Add-ons and cars',
  الفريق: 'Team',
  الإعدادات: 'Settings',
  الفواتير: 'Invoices',
  الدخول: 'Sign-in',
  'أنشأ الحجز {x}': 'created booking {x}',
  'تأكد الحجز {x}': 'booking {x} was confirmed',
  'ألغى الحجز {x}': 'cancelled booking {x}',
  'حوّل الحجز {x} إلى رصيد': 'converted booking {x} to credit',
  'سلّم السيارة للحجز {x}': 'handed over the car for booking {x}',
  'استلم السيارة من الحجز {x}': 'checked in the car from booking {x}',
  'غيّر موعد الحجز {x}': 'rescheduled booking {x}',
  'غيّر سيارة الحجز {x}': 'changed the car of booking {x}',
  'انتهت مهلة دفع الحجز {x}': 'the payment time of booking {x} ran out',
  'سُجّل عدم حضور للحجز {x}': 'booking {x} was flagged as a no-show',
  'تغيرت حالة الحجز {x}': 'booking {x} changed status',
  'دفعة ناجحة على الحجز {x}': 'payment received for booking {x}',
  'سجّل دفعة على الحجز {x}': 'recorded a payment for booking {x}',
  'فشلت دفعة على الحجز {x}': 'a payment failed for booking {x}',
  'استرد دفعة الحجز {x}': 'refunded a payment of booking {x}',
  'طلب استرداد دفعة الحجز {x}': 'requested a refund for booking {x}',
  'فشل استرداد دفعة الحجز {x}': 'a refund failed for booking {x}',
  'بدأ الدفع أونلاين للحجز {x}': 'started online payment for booking {x}',
  'نشر أسعاراً جديدة': 'published new prices',
  'عدّل سعر {x}': 'changed the price of {x}',
  'أضاف الموسم {x}': 'added season {x}',
  'عدّل الموسم {x}': 'edited season {x}',
  'حذف الموسم {x}': 'deleted season {x}',
  'أنشأ كود الخصم {x}': 'created promo code {x}',
  'عدّل كود الخصم {x}': 'edited promo code {x}',
  'حذف كود الخصم {x}': 'deleted promo code {x}',
  'استُخدم كود الخصم {x}': 'promo code {x} was used',
  'أغلق يوم {x} للحجز': 'closed {x} for bookings',
  'فتح يوم {x} للحجز': 'opened {x} for bookings',
  'أضاف الخدمة {x}': 'added add-on {x}',
  'عدّل الخدمة {x}': 'edited add-on {x}',
  'حذف الخدمة {x}': 'deleted add-on {x}',
  'أنشأ الباقة {x}': 'created pack {x}',
  'عدّل الباقة {x}': 'edited pack {x}',
  'حذف الباقة {x}': 'deleted pack {x}',
  'أضاف الموديل {x}': 'added model {x}',
  'عدّل الموديل {x}': 'edited model {x}',
  'أضاف السيارة {x} للأسطول': 'added car {x} to the fleet',
  'عدّل بيانات السيارة {x}': 'edited car {x}',
  'غيّر حالة السيارة {x}': 'changed the status of car {x}',
  'بدأ شحن السيارة {x}': 'started charging car {x}',
  'أنهى شحن السيارة {x}': 'finished charging car {x}',
  'خطّط صيانة للسيارة {x}': 'planned maintenance for car {x}',
  'أرسل السيارة {x} إلى الورشة': 'sent car {x} to the workshop',
  'أنهى صيانة السيارة {x}': 'finished maintenance of car {x}',
  'ألغى صيانة السيارة {x}': 'cancelled maintenance of car {x}',
  'اقترب موعد صيانة السيارة {x}': 'car {x} is due for service',
  'أضاف العضو {x}': 'added member {x}',
  'عدّل بيانات العضو {x}': 'edited member {x}',
  'أوقف حساب {x}': 'disabled the account of {x}',
  'فعّل حساب {x}': 'enabled the account of {x}',
  'حذف العضو {x}': 'deleted member {x}',
  'أرسل رابط دعوة إلى {x}': 'sent an invite link to {x}',
  'أنشأ الدور {x}': 'created role {x}',
  'عدّل صلاحيات الدور {x}': 'changed the permissions of role {x}',
  'حذف الدور {x}': 'deleted role {x}',
  'فعّل حسابه': 'activated their account',
  'نشر محتوى الموقع': 'published the site content',
  'عدّل محتوى الموقع': 'edited the site content',
  'عدّل قسماً في الموقع': 'edited a site section',
  'طلب تحويل الحجز {x} إلى رصيد': 'asked to convert booking {x} to credit',
  'اعتمد تحويل الحجز {x} إلى رصيد': 'approved converting booking {x} to credit',
  'رفض تحويل الحجز {x} إلى رصيد': 'rejected converting booking {x} to credit',
  'أضاف رصيداً {x}': 'added credit {x}',
  'استُخدم رصيد في الحجز {x}': 'credit was used on booking {x}',
  'انتهت صلاحية رصيد': 'credit expired',
  'أصدر الفاتورة {x}': 'issued invoice {x}',
  'أصدر الإيصال {x}': 'issued receipt {x}',
  'أصدر إشعار دائن {x}': 'issued credit note {x}',
  'أُضيف الزبون {x}': 'customer {x} was added',
  'عُدّلت بيانات الزبون {x}': 'customer {x} was updated',
  'دخل صفحة حجوزاتي': 'signed in to My bookings',
  'خرج من صفحة حجوزاتي': 'signed out of My bookings',
  'جُهّزت رسالة واتساب': 'a WhatsApp message was queued',
  'أُرسلت رسالة واتساب': 'a WhatsApp message was sent',
  'تعذّر إرسال رسالة واتساب': "a WhatsApp message couldn't be sent",
  'أُعيدت محاولة إرسال رسالة واتساب': 'a WhatsApp message was retried',
  'رُفع مستند': 'a document was uploaded',
  'بدأ رفع مستند': 'started a document upload',
  'حُذفت مستندات منتهية': 'expired documents were removed',
  'فتح مستنداً للزبون {x}': 'opened a document of customer {x}',
  'أضاف مصروفاً': 'added an expense',
  'عدّل مصروفاً': 'edited an expense',
  'ألغى مصروفاً': 'voided an expense',
  'رتّب لوحة اليوم': 'arranged the dashboard',
  'طلب تصدير تقرير': 'requested a report export',
  'جهز تقرير للتنزيل': 'a report is ready to download',
  'تعذّر تجهيز تقرير': "a report couldn't be prepared",
  'أُرسل رمز الدخول إلى {x}': 'a sign-in code was sent to {x}',
  'صدّر قائمة الحجوزات': 'exported the bookings list',
  'دفعة تحتاج مراجعة على الحجز {x}': 'a payment for booking {x} needs review',
  'استُخدم رصيد في دفع الحجز {x}': 'credit paid part of booking {x}',
  'أُلغي رصيد': 'credit was reversed',
  'سجّل عملاً منجزاً على السيارة {x}': 'recorded work done on car {x}',
  'أُرفقت صور حالة السيارة بالحجز {x}': 'photos of the car were added to booking {x}',
  'أُرفقت مستندات رُفعت عند التسليم بالحجز {x}': 'documents uploaded at handover were added to booking {x}',
};
